import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { BRAND, getProductImageVariant, getSetHero, firstCodedItem } from '../data/products';
import GoldRule from '../components/GoldRule';
import useProducts from '../hooks/useProducts';
import useProductImages, { cleanImage } from '../hooks/useProductImages';
import UnpackHero from '../components/UnpackHero';
import { getPhaseOffset } from '../lib/animPhase';
import ImageMarquee from '../components/motion/ImageMarquee';
import LeatherBackdrop from '../components/LeatherBackdrop';
import { Reveal, RevealLines, Stagger, StaggerItem } from '../components/motion/Reveal';
import HeroStage from '../components/HeroStage';
import CraftStrip from '../components/CraftStrip';
import TrustedByMarquee from '../components/TrustedByMarquee';

// Set card with hover-triggered image cycling through its pieces
function SetCard({ set, pieceImages }) {
  const allImages = pieceImages;
  // Desync each card's cycle from every other card's — see Sets.jsx Cover
  const cycleDuration = allImages.length * 1.8;
  const phaseOffset = getPhaseOffset(set.slug, cycleDuration);

  return (
    <Link
      to={`/sets/${set.slug}`}
      className="block h-full hairline border-subtle bg-mb-surface p-8 hover:border-gold transition-colors duration-300 group"
      data-testid={`home-set-card-${set.slug}`}
    >
      <div className="flex items-start justify-between mb-8">
        <div className="eyebrow-sm text-gold">{set.leather}</div>
        <div className="eyebrow-sm text-mb-muted">{set.pieceCount} pcs</div>
      </div>

      {/* Image frame with cross-fade cycling on hover */}
      <div className="relative aspect-[5/4] mb-8 overflow-hidden bg-mb hairline border-subtle group-hover:border-gold/40 transition-colors">
        {allImages.map((src, idx) => (
          <img
            key={src || idx}
            src={src}
            alt={`${set.name} piece ${idx + 1}`}
            className="absolute inset-0 w-full h-full object-cover transition-all duration-[800ms] ease-out group-hover:scale-[1.06]"
            style={{
              opacity: idx === 0 ? 1 : 0,
              animation: allImages.length > 1
                ? `set-img-cycle ${allImages.length * 1.8}s ease-in-out infinite ${idx * 1.8 + phaseOffset}s`
                : 'none',
            }}
            loading="lazy"
            onError={(e) => { e.currentTarget.style.opacity = '0'; }}
          />
        ))}
        {/* Hover reveal strip at bottom */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[0.5px] bg-gold origin-left transition-transform duration-500 ease-out"
          style={{ transform: 'scaleX(0)' }}
          data-hover-rule
        />
      </div>

      <h3 className="font-display text-2xl text-mb group-hover:text-gold transition-colors duration-300">
        {set.name}
      </h3>
      <p className="mt-3 font-display italic text-[14px] text-mb-muted leading-relaxed">
        {set.tagline}
      </p>
      <div className="mt-6 flex items-center justify-between">
        <span className="eyebrow-sm text-mb-muted">{set.leather}</span>
        <span className="eyebrow-sm text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          Explore →
        </span>
      </div>
    </Link>
  );
}

export default function Home() {
  const { visibleProducts: products, sets } = useProducts();
  const { manifest } = useProductImages();
  const featuredTiers = sets.filter((s) => !s.featured && !s.custom).slice(0, 3);
  const flagship = sets.find((s) => s.featured);
  const customSet = sets.find((s) => s.custom);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const copyY = useTransform(scrollYProgress, [0, 1], ['0px', '80px']);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const stageY = useTransform(scrollYProgress, [0, 1], ['0px', '40px']);
  const stageOpacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);

  // The flagship hero showcase — purpose-shot vertical composition
  const showcaseProduct = products['GV 4943'] || Object.values(products)[0];

  const showcaseSpecs = [
    'Full-grain Italian leather',
    '41 × 30 cm',
    'Saddle-stitched',
    'Fits 15.6" laptop',
  ];

  const marqueeItems = Object.values(products)
    .map((p) => ({ code: p.code, name: p.name, image: cleanImage(manifest, p.code) }))
    .filter((p) => p.image)
    .slice(0, 12);

  return (
    <div data-testid="home-page">
      {/* ── HERO ── */}
      <section
        ref={heroRef}
        className="relative min-h-[calc(100vh-72px)] flex items-center overflow-hidden"
        data-testid="home-hero"
      >
        <LeatherBackdrop variant="grain" intensity={0.48} drift={16} />

        {/* Two-column layout: text left, product stage right */}
        <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-[1fr_420px] xl:grid-cols-[1fr_480px] gap-8 md:gap-12 items-center min-h-[inherit]">

            {/* LEFT — copy */}
            <motion.div style={{ y: copyY, opacity: copyOpacity }}>
              <Reveal delay={0.05} y={12}>
                <div className="eyebrow text-mb-muted mb-10" data-testid="hero-eyebrow">
                  {BRAND.name}&nbsp;·&nbsp;Est. in leather
                </div>
              </Reveal>

              <h1
                className="font-display italic text-mb leading-[0.95] tracking-[-0.01em] text-5xl sm:text-6xl lg:text-[88px]"
                data-testid="hero-headline"
              >
                <RevealLines lines={['The room', 'always notices.']} delay={0.12} />
              </h1>

              <Reveal delay={0.5} className="mt-10">
                <GoldRule size="med" />
              </Reveal>

              <Reveal delay={0.58}>
                <p
                  className="mt-10 font-display italic text-[17px] md:text-[19px] text-mb-muted leading-relaxed max-w-lg"
                  data-testid="hero-subheading"
                >
                  Curated leather gift sets for the people who run things — cut
                  narrow, stitched slowly, presented without fanfare.
                </p>
              </Reveal>

              <Reveal delay={0.68} className="mt-14 flex flex-wrap items-center gap-6">
                <Link
                  to="/sets"
                  className="btn-outline-gold"
                  data-magnetic
                  data-testid="hero-view-sets-btn"
                >
                  View our sets
                  <ArrowUpRight size={14} strokeWidth={1.25} />
                </Link>
              </Reveal>

              {/* Tiny stats row */}
              <Reveal delay={0.82} className="mt-14 hidden md:flex items-center gap-8">
                {[
                  { num: '97', label: 'pieces in the house' },
                  { num: '4', label: 'curated sets' },
                  { num: '∞', label: 'custom builds' },
                ].map(({ num, label }) => (
                  <div key={label} className="flex items-baseline gap-2">
                    <span className="font-display text-[22px] text-gold">{num}</span>
                    <span className="eyebrow-sm text-mb-muted">{label}</span>
                  </div>
                ))}
              </Reveal>
            </motion.div>

            {/* RIGHT — product stage (desktop only) */}
            <motion.div
              className="hidden md:block"
              style={{ y: stageY, opacity: stageOpacity }}
            >
              <HeroStage
                image={getProductImageVariant('GV 4943', 'hero')}
                name={showcaseProduct?.name}
                specs={showcaseSpecs}
              />
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          aria-hidden="true"
          className="absolute left-1/2 bottom-8 -translate-x-1/2 hidden md:block"
          animate={{ y: [0, 10, 0], opacity: [0.35, 0.9, 0.35] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="w-[0.5px] h-12 bg-gold" />
        </motion.div>
      </section>

      {/* CRAFT PROCESS STRIP */}
      <CraftStrip />

      {/* SCROLL-UNPACK FLAGSHIP */}
      <UnpackHero set={flagship} products={products} />

      {/* PHOTOGRAPHY MARQUEE */}
      <section className="hairline-t border-subtle py-20 md:py-28" data-testid="home-marquee">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-end justify-between mb-12">
          <div>
            <div className="eyebrow text-gold mb-5">The house catalogue</div>
            <h2 className="font-display text-3xl md:text-4xl text-mb">
              Twelve pieces, <span className="italic">one grammar.</span>
            </h2>
          </div>
          <Link
            to="/products"
            className="eyebrow text-mb-muted hover:text-gold transition-colors hidden md:inline-flex"
            data-testid="home-all-products-link"
          >
            All pieces →
          </Link>
        </div>
        <ImageMarquee items={marqueeItems} height={260} />
        <div className="mt-5">
          <ImageMarquee items={[...marqueeItems].reverse()} height={160} reverse baseSpeed={18} />
        </div>
      </section>

      {/* MANIFESTO STRIP */}
      <section className="hairline-t border-subtle" data-testid="home-manifesto">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <div className="eyebrow text-gold">On the house style</div>
          </Reveal>
          <div className="md:col-span-8">
            <h3 className="font-display text-[22px] md:text-[30px] leading-[1.5] text-mb">
              <RevealLines
                lines={[
                  'We do not design for the boardroom.',
                  'We design for the moment after the meeting —',
                  'when the wallet lands on the bar, when the bag',
                  'is set beside the chair, when the room quietly',
                  'works out who you are.',
                ]}
              />
            </h3>
            <Reveal delay={0.2} className="mt-10">
              <GoldRule />
            </Reveal>
            <Reveal delay={0.26} className="mt-8">
              <div className="eyebrow-sm text-mb-muted">
                Cut in Italian leather. Assembled slowly.
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE SETS — preview */}
      <section className="hairline-t border-subtle" data-testid="home-sets-preview">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex items-end justify-between mb-16">
            <Reveal>
              <div className="eyebrow text-mb-muted mb-6">The collection</div>
              <h2 className="font-display text-4xl md:text-5xl text-mb">
                Selected, not stacked.
              </h2>
            </Reveal>
            <Link
              to="/sets"
              className="eyebrow text-gold hover:text-mb transition-colors hidden md:inline-flex items-center gap-2"
              data-testid="home-all-sets-link"
            >
              All sets →
            </Link>
          </div>

          <Stagger className="grid md:grid-cols-3 gap-8">
            {featuredTiers.map((set) => {
              // Collect up to 4 styled piece images for hover cycling
              const pieceImages = (set.items || [])
                .slice(0, 4)
                .map((code) => cleanImage(manifest, firstCodedItem(set, products) !== code ? code : code))
                .filter(Boolean);
              return (
                <StaggerItem key={set.slug}>
                  <SetCard set={set} pieceImages={pieceImages} />
                </StaggerItem>
              );
            })}
            {customSet && (
              <StaggerItem>
                <Link
                  to={`/sets/${customSet.slug}`}
                  className="block h-full hairline border-subtle bg-mb p-8 hover:border-gold transition-colors group"
                  data-testid={`home-set-card-${customSet.slug}`}
                >
                  <div className="flex items-start justify-between mb-8">
                    <div className="eyebrow-sm text-gold">Build your own</div>
                    <div className="eyebrow-sm text-mb-muted">Free hand</div>
                  </div>
                  <div className="relative aspect-[5/4] mb-8 bg-mb-surface hairline border-subtle flex items-center justify-center overflow-hidden group-hover:border-gold transition-colors">
                    <motion.span
                      className="font-display text-[52px] leading-none text-gold"
                      animate={{ rotate: [0, 90, 180, 270, 360] }}
                      transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                      style={{ opacity: 0.65 }}
                    >
                      +
                    </motion.span>
                  </div>
                  <h3 className="font-display text-2xl text-mb group-hover:text-gold transition-colors">
                    {customSet.name}
                  </h3>
                  <p className="mt-3 font-display italic text-[14px] text-mb-muted">
                    {customSet.tagline}
                  </p>
                  <div className="mt-6 eyebrow-sm text-mb-muted">
                    Three, five, seven — or every piece in the house
                  </div>
                </Link>
              </StaggerItem>
            )}
          </Stagger>

          {flagship && (
            <Reveal delay={0.1} className="mt-8">
              <Link
                to={`/sets/${flagship.slug}`}
                className="block hairline border-gold bg-mb-surface p-8 md:p-12 hover:bg-mb-rich transition-colors group"
                data-testid="home-flagship-card"
              >
                <div className="grid md:grid-cols-12 gap-10 items-center">
                  <div className="md:col-span-5">
                    <div className="eyebrow text-gold">The house at full measure</div>
                    <h3 className="mt-8 font-display text-3xl md:text-5xl text-mb">
                      {flagship.name}
                    </h3>
                    <div className="mt-6">
                      <GoldRule />
                    </div>
                    <p className="mt-6 font-display italic text-[18px] text-mb-muted">
                      {flagship.tagline}
                    </p>
                    <div className="mt-10 flex flex-wrap gap-3">
                      <span className="feature-badge">{flagship.leather}</span>
                      <span className="feature-badge">{flagship.pieceCount} pieces</span>
                      {flagship.laptop && (
                        <span className="feature-badge">{flagship.laptop} laptop</span>
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-7">
                    <div className="relative aspect-[16/9] overflow-hidden bg-mb hairline border-subtle">
                      <img
                        src={getSetHero(flagship.slug)}
                        alt={flagship.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.opacity = 0;
                        }}
                      />
                    </div>
                    <div className="mt-6 flex items-center justify-end">
                      <span className="eyebrow text-gold group-hover:text-mb transition-colors">
                        Open the set →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </Reveal>
          )}
        </div>
      </section>

      {/* GIFTING IN QUANTITY */}
      <section className="hairline-t border-subtle" data-testid="home-corporate-tease">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <Reveal>
              <div className="eyebrow text-gold mb-6">Gifting at scale</div>
            </Reveal>
            <h2 className="font-display text-4xl md:text-5xl text-mb leading-[1.1]">
              <RevealLines lines={['We outfit']} />
              <RevealLines
                lines={['the people who run things.']}
                delay={0.1}
                lineClassName="italic"
              />
            </h2>
            <Reveal delay={0.2}>
              <p className="mt-10 text-[15px] text-mb-muted max-w-xl leading-relaxed">
                Boards, partners, retiring directors, incoming hires. We build quiet,
                embossed sets for the occasions a company cannot afford to hand off to
                procurement. Open any set below and enquire in quantity.
              </p>
            </Reveal>
            <Reveal delay={0.26} className="mt-10">
              <Link
                to="/sets"
                className="btn-outline-gold"
                data-magnetic
                data-testid="home-corporate-cta"
              >
                Browse the sets
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="md:col-span-5 hidden md:flex items-center">
            <div className="w-full hairline border-subtle p-8 bg-mb-surface">
              <div className="eyebrow text-mb-muted mb-6">On the record</div>
              <div className="space-y-5 font-display italic text-[18px] text-mb leading-relaxed">
                <p>
                  “Seven of my partners received The Full Floor at their anniversary.
                  Three asked where we ordered dinner.”
                </p>
                <div className="eyebrow-sm text-mb-muted not-italic">
                  — Managing Partner, NY
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUSTED BY — quiet closing strip, kept understated on the homepage */}
      <section
        className="hairline-t border-subtle py-10"
        data-testid="home-trusted-by"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center gap-5 md:gap-10">
          <div className="eyebrow-sm text-mb-muted whitespace-nowrap">
            In rooms like these
          </div>
          <TrustedByMarquee variant="subtle" />
        </div>
      </section>
    </div>
  );
}
